import i18n from 'i18next';
import {Alert, ProgressBar} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {EntityResourceResolver} from '../../context/EntityResourceResolver';
import {type BulkOperationResponseBody} from '../../types/api/bulkOperations';
import {type BulkOperationActionDefinition} from '../../types/BulkOperationActionDefinition';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {getBulkOperationProcessedCount} from '../../utils/getBulkOperationProcessedCount';
import {isBulkOperationTerminal} from '../../utils/isBulkOperationTerminal';
import {BulkOperationCounts} from './BulkOperationCounts';
import {BulkOperationStatusTabs} from './BulkOperationStatusTabs';

function getOperationError(operation: BulkOperationResponseBody): string {
  // The bulk-operation API contract defines errorMessage as user-facing text, not exception details.
  return (
    operation.errorMessage ??
    [
      `${i18n.t('general.notifications.success')}: ${operation.doneCount}`,
      `${i18n.t('entity.automationRule.detail.runResult.skipped')}: ${operation.skippedCount}`,
      `${i18n.t('general.notifications.labels.failed')}: ${operation.failedCount}`,
    ].join(' · ')
  );
}

function BulkOperationProgress(props: {operation: BulkOperationResponseBody}) {
  const processedCount = getBulkOperationProcessedCount(props.operation);
  const percentage =
    props.operation.totalCount === 0 ? 0 : processedCount / props.operation.totalCount;

  return (
    <VStack spacing={3}>
      <Text>{i18n.t('general.labels.inProgress')}</Text>
      <ProgressBar percentage={percentage} />
      <BulkOperationCounts operation={props.operation} />
    </VStack>
  );
}

export function BulkOperationResult(props: {
  services: BulkOperationsServices;
  entityResourceResolver?: EntityResourceResolver;
  actionDefinition: BulkOperationActionDefinition;
  operation: BulkOperationResponseBody | undefined;
  testId?: string;
}) {
  const operation = props.operation;
  if (operation === undefined) {
    return null;
  }
  if (!isBulkOperationTerminal(operation.state)) {
    return <BulkOperationProgress operation={operation} />;
  }

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <Show
        when={
          operation.state !== 'DONE' &&
          (operation.state !== 'PARTIALLY_DONE' ||
            isNotNilOrEmpty(operation.errorCode) ||
            isNotNilOrEmpty(operation.errorMessage))
        }
      >
        <Alert
          variant="error"
          title={
            operation.state === 'PARTIALLY_DONE'
              ? i18n.t('entity.condition.notifications.finishedWithWarnings')
              : `${props.actionDefinition.title} · ${i18n.t('general.notifications.labels.failed')}`
          }
          message={getOperationError(operation)}
        />
      </Show>
      <BulkOperationStatusTabs
        services={props.services}
        entityResourceResolver={props.entityResourceResolver}
        key={operation.bulkOperationId}
        operation={operation}
        successMessage={props.actionDefinition.doneLabel}
        testId={props.testId}
      />
    </div>
  );
}
